// RESOURCES domain: one global Assets (IA §14–34, backend §30–37).
// Every contextual "Assets" view (workspace, brand, project, picker) is a filter over the same rows.
import { db, byId, eq, sortBy, uniq, insert, update, removeLocal, whenPersisted, tableLoadState, refreshTable, ORG_ID } from './core';
import { pagedTable } from './paged';
import { forgetRuns } from './runs';

export const SCOPES = ['SYSTEM', 'ORGANIZATION', 'WORKSPACE', 'BRAND', 'PROJECT'];
export const SCOPE_LABEL: any = { SYSTEM: 'System', ORGANIZATION: 'Shared', WORKSPACE: 'Workspace', BRAND: 'Brand', PROJECT: 'Project' };

// Assets type chips (IA §27) → asset.type groups
export const TYPE_GROUPS = [
  ['all', 'All', null],
  ['images', 'Images', ['IMAGE', 'REFERENCE']],
  ['video', 'Video', ['VIDEO']],
  ['text', 'Text', ['TEXT', 'PROMPT']],
  ['audio', 'Audio', ['AUDIO']],
  ['3d', '3D', ['3D', 'MODEL_3D', 'MESH']],
  ['prompts', 'Prompts', ['PROMPT']],
  ['models', 'Models', ['MODEL', 'LORA', 'TOOL', 'VOICE']],
  ['effects', 'Effects', ['EFFECT']],
  ['templates', 'Templates', ['TEMPLATE', 'FLOW_TEMPLATE', 'NODE_TEMPLATE']],
  ['characters', 'Characters', ['CHARACTER']],
  ['styles', 'Styles & Presets', ['STYLE', 'PRESET', 'FONT']],
  ['more', 'More', ['AUDIO', 'FILE', 'DATASET', 'INTEGRATION_PRESET']],
];

export const getAsset = (id?: any) => byId('assets', id);
export const assetVersions = (id?: any) => sortBy(eq('asset_versions', 'asset_id', id), 'version');

export function matchesAssetGroup(asset: any, group: string) {
  const types = TYPE_GROUPS.find(([key]: any) => key === group)?.[2];
  if (!types) return true;
  if (types.includes(String(asset.type || '').toUpperCase())) return true;
  const mime = String(asset.mime || asset.mime_type || asset.metadata?.mime || '').toLowerCase();
  const paths = [asset.object_key, asset.external_url, asset.name].filter(Boolean);
  const hasExtension = (pattern: RegExp) => paths.some(path => pattern.test(String(path)));
  if (group === 'text') return mime.startsWith('text/') || hasExtension(/\.(?:txt|md|markdown)(?:$|[?#])/i);
  if (group === 'audio') return mime.startsWith('audio/') || hasExtension(/\.(?:mp3|wav|ogg|m4a|flac|aac)(?:$|[?#])/i);
  if (group === '3d') return mime.startsWith('model/') ||
    ['3d', 'model_3d', 'mesh', 'glb', 'gltf', 'obj', 'fbx', 'usdz', 'stl', 'ply', 'splat', 'spz'].includes(String(asset.subtype || asset.metadata?.format || asset.metadata?.media || '').toLowerCase()) ||
    hasExtension(/\.(?:glb|gltf|obj|fbx|usdz|stl|ply|splat|spz)(?:$|[?#])/i);
  return false;
}

// Brand context of an asset: own brand, or the brand of its project (so Brand=Koloblox includes Koloblox project assets, seed §41)
export function assetBrandIds(a?: any) {
  if (a.brand_id) return [a.brand_id];
  if (a.project_id) {
    const project = byId('projects', a.project_id);
    const brandId = project?.brand_id || project?.data?.brand_id;
    return brandId ? [brandId] : [];
  }
  return [];
}

export const assetScope = (a: any) => a.scope || a.data?.scope || (a.project_id ? 'PROJECT' : a.brand_id ? 'BRAND' : a.workspace_id ? 'WORKSPACE' : 'ORGANIZATION');

// Human context label: "Koloblox", "Koloblox Website", "Shared", "System", "Creative Studio"
export function ownerLabel(a?: any) {
  const scope = assetScope(a);
  if (scope === 'BRAND') return byId('brands', a.brand_id)?.name;
  if (scope === 'PROJECT') return byId('projects', a.project_id)?.name;
  if (scope === 'WORKSPACE') return byId('workspaces', a.workspace_id)?.name;
  return SCOPE_LABEL[scope];
}

export function usage(assetId?: any) {
  const rows = eq('asset_usages', 'asset_id', assetId);
  const projects = uniq(rows.map((r?: any) => r.project_id));
  const flows = uniq(rows.map((r?: any) => r.flow_id));
  const tree = projects.map((pid?: any) => ({
    project: byId('projects', pid) || { id: pid, name: 'Project' },
    flows: uniq(rows.filter((r?: any) => r.project_id === pid).map((r?: any) => r.flow_id)).map((fid?: any) => ({
      flow: byId('flows', fid) || { id: fid, name: rows.find((r: any) => r.flow_id === fid)?.flow_name || 'Flow' },
      nodes: rows.filter((r?: any) => r.flow_id === fid).map((r?: any) => ({ node: { id: r.node_id, name: r.node_name || r.node_id }, version_ref: r.version_ref })),
    })),
  }));
  const state = tableLoadState('asset_usages');
  return { projects: projects.length, flows: flows.length, nodes: rows.length, tree, rows, loading: !state.ready, error: state.error };
}

// Assets are paged from the server; filters, search and totals run there (asset_list view).
export interface AssetFilters {
  q?: string; group?: string; scope?: string; brandId?: string; brandOwnerId?: string; workspaceId?: string; projectId?: string;
  availableIn?: string; type?: string; types?: string[]; status?: string; creator?: string; family?: string; used?: string;
  includeArchived?: boolean; outputsOnly?: boolean; sort?: string; limit?: number;
}
const MEDIA_GROUPS = ['images', 'video', 'text', 'audio', '3d'];
function assetQuery(f: AssetFilters): URLSearchParams {
  const params = new URLSearchParams();
  const groupTypes = f.group && !MEDIA_GROUPS.includes(f.group) ? TYPE_GROUPS.find(([key]: any) => key === f.group)?.[2] as string[] | null : null;
  const entries: [string, unknown][] = [['q', f.q?.trim()], ['group', f.group && MEDIA_GROUPS.includes(f.group) ? f.group : null],
    ['types', (f.types || groupTypes)?.join(',')], ['scope', f.scope], ['brand', f.brandId], ['brand_id', f.brandOwnerId],
    ['workspace', f.workspaceId], ['project', f.projectId], ['available', f.availableIn], ['type', f.type], ['status', f.status],
    ['creator', f.creator], ['family', f.family], ['outputs', f.outputsOnly ? '1' : null], ['archived', f.includeArchived ? '1' : null], ['sort', f.sort === 'name' ? 'name' : null]];
  for (const [key, value] of entries) if (value) params.set(key, String(value));
  return params;
}
// 'used' and 'usage' read the prototype asset_usages table, so they only narrow the loaded rows.
const pageFilters = ({ used, group, ...filters }: AssetFilters): AssetFilters => ({ ...filters, group: group === 'all' ? undefined : group });
const assetPages = pagedTable<AssetFilters>('assets', assetQuery);

export function isLibraryAsset(asset: any) {
  return asset.data?.saved_to_library === true || asset.saved_to_library === true ||
    (!asset.execution_id && !['generation', 'output-node', 'inline-output'].includes(asset.data?.origin));
}

export function listAssets(filters: AssetFilters = {}) {
  let rows = assetPages.list(pageFilters(filters));
  if (!filters.outputsOnly) rows = rows.filter(isLibraryAsset);
  if (filters.used === 'used') rows = rows.filter((a?: any) => db.asset_usages.some((u?: any) => u.asset_id === a.id));
  if (filters.used === 'unused') rows = rows.filter((a?: any) => !db.asset_usages.some((u?: any) => u.asset_id === a.id));
  return rows;
}
export const loadAssets = (filters: AssetFilters = {}) => assetPages.load(pageFilters(filters));
export const loadMoreAssets = (filters: AssetFilters = {}) => assetPages.more(pageFilters(filters));
export const assetListState = (filters: AssetFilters = {}) => assetPages.state(pageFilters(filters));
/** Server total for these filters; undefined while it loads. */
export const assetCount = (filters: AssetFilters = {}) => assetPages.count(pageFilters(filters));
export const refreshAssets = () => Promise.all([assetPages.refresh(), refreshTable('asset_usages')]);
export const forgetAssets = () => assetPages.forget();

export function typeCounts(filters: AssetFilters = {}) {
  return Object.fromEntries(TYPE_GROUPS.map(([k]: any) => [k, assetCount({ ...filters, group: k })]));
}

// Availability in a workspace (IA §36): system + org + this workspace + brands linked to it + its projects
export function isAvailableInWorkspace(a?: any, wsId?: any) {
  const scope = assetScope(a);
  if (scope === 'SYSTEM' || scope === 'ORGANIZATION') return true;
  if (scope === 'WORKSPACE') return a.workspace_id === wsId;
  if (scope === 'BRAND') return db.workspace_brands.some((r?: any) => r.workspace_id === wsId && r.brand_id === a.brand_id)
    || db.projects.some(project => project.workspace_id === wsId && (project.brand_id || project.data?.brand_id) === a.brand_id);
  if (scope === 'PROJECT') return byId('projects', a.project_id)?.workspace_id === wsId;
  return false;
}

// availableAssets = system + organization + workspace + project brands + project (IA §25)
export function availableAssets(projectId?: any, filters: AssetFilters = {}) {
  return projectId ? listAssets({ ...filters, availableIn: projectId }) : [];
}

// Contextual recommendations (IA §26, seed §43–44): assets actually used in this project first,
// weighted towards the project's own and brand context. Other brands never appear.
// Mix contexts instead of letting heavily reused shared models crowd out brand/project context:
// quota per bucket (brand 2 · project 2 · workspace/shared 1 · system 1), then fill by usage.
export function recommendedAssets(projectId?: any, { limit = 6, types }: any = {}) {
  const typeRank: any = { CHARACTER: 0, PROMPT: 1, STYLE: 2 };
  const pool = availableAssets(projectId).filter((a?: any) => !types || types.includes(a.type)).map((a?: any) => ({
    a, uses: db.asset_usages.filter((u?: any) => u.asset_id === a.id && u.project_id === projectId).length,
  }));
  const rank = (x?: any, y?: any) => y.uses - x.uses || (typeRank[x.a.type] ?? 9) - (typeRank[y.a.type] ?? 9) || (x.a.created_at < y.a.created_at ? -1 : 1);
  const used = pool.filter((x?: any) => x.uses > 0).sort(rank);
  const unusedProject = pool.filter((x?: any) => !x.uses && x.a.scope === 'PROJECT' && x.a.status === 'Approved').sort(rank);
  const picked: typeof pool = [];
  const take = (pred?: any, n?: any, from: any = used) => from.filter((x?: any) => pred(x.a) && !picked.includes(x)).slice(0, n).forEach((x?: any) => picked.push(x));
  take((a?: any) => a.scope === 'BRAND', 2);
  take((a?: any) => a.scope === 'PROJECT', 2);
  take((a?: any) => a.scope === 'PROJECT', 2 - picked.filter((x?: any) => x.a.scope === 'PROJECT').length, unusedProject);
  take((a?: any) => a.scope === 'WORKSPACE', 1);
  if (!picked.some((x?: any) => x.a.scope === 'WORKSPACE')) take((a?: any) => a.scope === 'ORGANIZATION', 1);
  take((a?: any) => a.scope === 'SYSTEM', 1);
  take(() => true, limit - picked.length);
  return picked.slice(0, limit).map((x?: any) => x.a);
}

export function pickerSections(projectId?: any, { q, tab = 'recommended', types, recents = [], favorites = [] }: any = {}) {
  let pool = q ? listAssets({ q }) : availableAssets(projectId); // search spans the whole accessible Assets
  if (types) pool = pool.filter((a?: any) => types.includes(a.type));
  switch (tab) {
    case 'recommended': return q ? pool : recommendedAssets(projectId, { limit: 12, types });
    case 'used': return pool.filter((a?: any) => db.asset_usages.some((u?: any) => u.asset_id === a.id && u.project_id === projectId));
    case 'recent': { const ids = recents.filter((r?: any) => r.entity_type === 'asset').map((r?: any) => r.entity_id); return pool.filter((a?: any) => ids.includes(a.id)); }
    case 'favorites': return pool.filter((a?: any) => favorites.some((f?: any) => f.entity_type === 'asset' && f.entity_id === a.id));
    default: return pool.filter((a?: any) => a.scope === tab);
  }
}

export function relatedFamily(a?: any) {
  if (!a.family) return [];
  return listAssets({ family: a.family, limit: 24 }).filter((x?: any) => x.id !== a.id);
}

// Canvas outputs record run_id and project_id; Flow API outputs record execution_id and rely on the asset's project.
export function lineageRefs(l: any, asset: any) {
  return { projectId: l.project_id ?? asset.project_id, flowId: l.flow_id, runId: l.run_id ?? l.execution_id };
}

export function lineage(a?: any) {
  const l = a.metadata?.lineage;
  if (!l) return null;
  const refs = lineageRefs(l, a);
  return { ...l, project: byId('projects', refs.projectId), flow: byId('flows', refs.flowId), run: refs.runId ? { id: refs.runId, number: String(refs.runId).slice(0, 8) } : null };
}

// ── mutations (in-memory; backend: PATCH /api/assets/:id etc.) ──
export function archiveAsset(id?: any) { update('assets', id, { status: 'Archived', archived_at: new Date().toISOString() }); }
export async function deleteAssets(ids: string[]): Promise<number> {
  const uniqueIds = [...new Set(ids)];
  if (!uniqueIds.length) throw new Error('Select at least one asset to delete.');
  const response = await fetch('/api/v1/assets/bulk-delete', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: uniqueIds }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Could not delete assets (${response.status})`);
  const confirmed = new Set(Array.isArray(body.deleted) ? body.deleted : []);
  if (uniqueIds.some(id => !confirmed.has(id))) throw new Error(body.error || 'The server did not confirm deletion of every selected asset.');
  uniqueIds.forEach(id => removeLocal('assets', id));
  db.asset_usages = db.asset_usages.filter((usage?: any) => !uniqueIds.includes(usage.asset_id));
  // Execution pages must offer Save again after a result leaves the library.
  forgetRuns();
  forgetAssets();
  return uniqueIds.length;
}

export async function deleteAsset(id: string): Promise<number> {
  return deleteAssets([id]);
}
export function replaceReferences(fromId?: any, toId?: any) {
  const rows = db.asset_usages.filter((u?: any) => u.asset_id === fromId);
  rows.forEach((u?: any) => {
    u.asset_id = toId;
    const n = byId('nodes', u.node_id);
    if (n?.config?.asset_refs) n.config.asset_refs = n.config.asset_refs.map((r?: any) => (r.asset_id === fromId ? { asset_id: toId, version: 'latest' } : r));
  });
  return rows.length;
}
export function setAssetStatus(id?: any, status?: any) { update('assets', id, { status, updated_at: new Date().toISOString() }); }
export async function renameAsset(id: string, name: string) {
  const nextName = name.trim();
  if (!nextName) throw new Error('Asset name cannot be empty.');
  if (nextName.length > 255) throw new Error('Asset name must be 255 characters or fewer.');
  const asset = update('assets', id, { name: nextName, updated_at: new Date().toISOString() });
  if (!asset) throw new Error('Asset not found.');
  try { await whenPersisted(asset); }
  catch (error) { await refreshAssets().catch((): void => undefined); throw error; }
  return asset;
}
export function promoteAsset(id?: any, brandId?: any) { update('assets', id, { scope: 'BRAND', brand_id: brandId, project_id: null, updated_at: new Date().toISOString() }); }
export function createAsset({ name, type, scope, workspaceId = null, brandId = null, projectId = null, createdBy }: any) {
  const id = 'ast_new_' + Date.now();
  const now = new Date().toISOString();
  insert('asset_versions', { id: id + '_v1', asset_id: id, version: 1, status: 'current', created_at: now });
  return insert('assets', { id, organization_id: ORG_ID, name, description: '', type, subtype: null, scope, workspace_id: workspaceId, brand_id: brandId, project_id: projectId, status: 'Draft', provider: null, thumbnail: { hue: [...name].reduce((h?: any, c?: any) => (h * 31 + c.charCodeAt(0)) % 360, 7), glyph: type.toLowerCase() }, metadata: {}, tags: [], created_by: createdBy, created_at: now, updated_at: now, current_version: 1 });
}

export function setCurrentVersion(assetId?: any, version?: any) {
  eq('asset_versions', 'asset_id', assetId).forEach((v?: any) => { if (v.version === version) v.status = 'current'; else if (v.status === 'current') v.status = 'deprecated'; });
  update('assets', assetId, { current_version: version, updated_at: new Date().toISOString() });
}
export function createVersion(assetId?: any, userId?: any, note: any = 'New upload') {
  const a = byId('assets', assetId);
  const version = Math.max(...eq('asset_versions', 'asset_id', assetId).map((v?: any) => v.version)) + 1;
  eq('asset_versions', 'asset_id', assetId).forEach((v?: any) => { if (v.status === 'current') v.status = 'deprecated'; });
  insert('asset_versions', { id: `${assetId}_v${version}`, asset_id: assetId, version, status: 'current', metadata: { note }, created_by: userId, created_at: new Date().toISOString() });
  update('assets', assetId, { current_version: version, status: 'Review', updated_at: new Date().toISOString() });
  return version;
}
export const ASSET_TYPES = ['IMAGE', 'VIDEO', 'AUDIO', 'TEXT', 'MODEL_3D', 'FONT', 'FILE', 'PROMPT', 'PRESET', 'STYLE', 'MODEL', 'LORA', 'VOICE', 'EFFECT', 'TEMPLATE', 'FLOW_TEMPLATE', 'NODE_TEMPLATE', 'CHARACTER', 'REFERENCE', 'DATASET', 'TOOL', 'INTEGRATION_PRESET'];
export const ASSET_STATUSES = ['Draft', 'Review', 'Approved', 'Deprecated', 'Archived'];
export const auditFor = (entityId?: any) => sortBy(eq('audit_events', 'entity_id', entityId), 'created_at');

// Creators are offered from all members: the library is no longer loaded whole to find who uploaded.
export const assetCreators = () => db.users.filter((u?: any) => u.name);
